import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

const RETRY_BACKOFF_MS = [1000, 2000, 4000];
const MAX_RETRIES = RETRY_BACKOFF_MS.length;

function isTransientError(errorCode: number | undefined): boolean {
	// MEDIA_ERR_NETWORK (2) or MEDIA_ERR_DECODE (3) are transient and worth retrying.
	// MEDIA_ERR_ABORTED (1) and MEDIA_ERR_SRC_NOT_SUPPORTED (4) are fatal.
	return errorCode === MediaError.MEDIA_ERR_NETWORK || errorCode === MediaError.MEDIA_ERR_DECODE;
}

export function useMuxRetry({
	videoRef,
	trackVideoError,
}: {
	videoRef: React.RefObject<HTMLVideoElement | null>;
	trackVideoError: (e: unknown) => void;
}) {
	const [retryKey, setRetryKey] = useState(0);
	const retryCount = useRef(0);
	const retryTimerId = useRef<ReturnType<typeof setTimeout> | null>(null);
	const mountedRef = useRef(true);

	useEffect(() => {
		mountedRef.current = true;
		return () => {
			mountedRef.current = false;
			if (retryTimerId.current !== null) {
				clearTimeout(retryTimerId.current);
				retryTimerId.current = null;
			}
		};
	}, []);

	const handleError = useCallback(() => {
		if (retryTimerId.current !== null) {
			clearTimeout(retryTimerId.current);
			retryTimerId.current = null;
		}

		const errorCode = videoRef.current?.error?.code;

		if (!isTransientError(errorCode)) {
			trackVideoError(new Error(`Video element error (code=${errorCode ?? 'unknown'})`));
			return;
		}

		if (retryCount.current >= MAX_RETRIES) {
			trackVideoError(new Error(`Video network error after ${MAX_RETRIES} retries (code=${errorCode})`));
			return;
		}

		const delay = RETRY_BACKOFF_MS[retryCount.current] ?? RETRY_BACKOFF_MS[RETRY_BACKOFF_MS.length - 1];

		retryTimerId.current = setTimeout(() => {
			retryTimerId.current = null;
			if (!mountedRef.current) return;
			retryCount.current += 1;
			setRetryKey((k) => k + 1);
		}, delay);
	}, [videoRef, trackVideoError]);

	const handlePlaying = useCallback(() => {
		if (retryTimerId.current !== null) {
			clearTimeout(retryTimerId.current);
			retryTimerId.current = null;
		}
		if (retryCount.current > 0) {
			retryCount.current = 0;
		}
	}, []);

	return { retryKey, handleError, handlePlaying };
}
